/**
 * BGV API Service for Admin Dashboard
 * 
 * This service provides a clean interface for the admin dashboard to interact
 * with BGV's existing edge functions and database.
 * 
 * SAFETY: All production calls require explicit confirmation to avoid accidental posts
 */

import { supabase } from '@/lib/supabase'

// Use shared Supabase client - both BGV and admin dashboard use same database and functions
// No need for separate client since we share the same Supabase project

// Type definitions
export interface CreateInstagramPostRequest {
  postId: string
  testMode?: boolean // Safety flag to prevent real posts
}

export interface CreateInstagramPostResponse {
  success: boolean
  data?: any
  error?: string
  status_code?: number
  timestamp?: string
}

export interface ProcessScheduledPostsResponse {
  message: string
  processed: number
  results: Array<{
    id: string
    success: boolean
    error?: string
  }>
}

export interface BGVSocialPost {
  id: string
  content: string
  caption: string | null
  image_urls: string[]
  status: 'DRAFT' | 'SCHEDULED' | 'PROCESSING' | 'QUEUED' | 'POSTED' | 'ERROR'
  workflow_stage: string
  scheduled_time: string | null
  published_at: string | null
  profile_id: string | null
  platform_specific_data: any
  processing_logs: any[]
  error_message: string | null
  last_status_change: string
  created_at: string
  updated_at: string
  hashtags?: string[]
  tags?: string[]
  ai_generated: boolean
  approval_required: boolean
  created_by: string
}

export interface BGVSocialProfile {
  id: string
  name: string
  platform: string
  franchise_region: string
  account_id: string | null
  username: string | null
  is_active: boolean
  connection_status: string
  created_at: string
  updated_at: string
}

export interface PostFilters {
  status?: string[]
  profile_id?: string
  date_range?: {
    start: string
    end: string
  }
  created_by?: string
}

export class BGVApiService {
  private readonly functionsUrl = import.meta.env.VITE_SUPABASE_URL + '/functions/v1'

  /**
   * Test connection to BGV services
   */
  async testConnection(): Promise<{ database: boolean; functions: boolean }> {
    try {
      // Test database connection
      const { error: dbError } = await supabase.from('social_posts').select('count').limit(1)
      const database = !dbError

      // Test functions connection
      const { error: funcError } = await supabase.functions.invoke('process-scheduled-posts', {
        body: {}
      })
      const functions = !funcError

      return { database, functions }
    } catch (error) {
      console.error('BGV connection test failed:', error)
      return { database: false, functions: false }
    }
  }

  /**
   * Create and publish an Instagram post using BGV's edge function
   * 
   * SAFETY: Requires explicit confirmation for production posts
   */
  async createInstagramPost(
    postId: string, 
    options: { testMode?: boolean; confirmProduction?: boolean } = {}
  ): Promise<CreateInstagramPostResponse> {
    try {
      // Safety check for production posts
      if (!options.testMode && !options.confirmProduction) {
        throw new Error('Production posts require explicit confirmation. Set confirmProduction: true or testMode: true')
      }

      // If in test mode, update post to indicate it's a test
      if (options.testMode) {
        await this.markPostAsTest(postId)
      }

      const { data, error } = await supabase.functions.invoke('create-instagram-post', {
        body: { 
          postId,
          testMode: options.testMode 
        }
      })

      if (error) {
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('BGV createInstagramPost error:', error)
      throw error
    }
  }

  /**
   * Process all scheduled posts that are due for publishing
   */
  async processScheduledPosts(): Promise<ProcessScheduledPostsResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('process-scheduled-posts', {
        body: {}
      })

      if (error) {
        throw new Error(error.message)
      }

      return data
    } catch (error) {
      console.error('BGV processScheduledPosts error:', error)
      throw error
    }
  }

  /**
   * Create a new social post in BGV database
   */
  async createPost(postData: {
    content: string
    caption?: string
    image_urls: string[]
    profile_id: string
    scheduled_time?: string
    hashtags?: string[]
    tags?: string[]
    testMode?: boolean
  }): Promise<BGVSocialPost> {
    try {
      const post: Partial<BGVSocialPost> = {
        content: postData.content,
        caption: postData.caption || null,
        image_urls: postData.image_urls,
        status: 'DRAFT',
        workflow_stage: 'DRAFT',
        profile_id: postData.profile_id,
        scheduled_time: postData.scheduled_time || null,
        hashtags: postData.hashtags || [],
        tags: postData.tags || [],
        ai_generated: false,
        approval_required: true,
        created_by: postData.testMode ? '00000000-0000-0000-0000-000000000001' : '00000000-0000-0000-0000-000000000002'
      }

      // Add test marker if in test mode
      if (postData.testMode) {
        post.content = '[TEST] ' + post.content
        post.caption = post.caption ? '[TEST] ' + post.caption : '[TEST POST - DO NOT PUBLISH]'
      }

      const { data, error } = await supabase
        .from('social_posts')
        .insert(post)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data
    } catch (error) {
      console.error('BGV createPost error:', error)
      throw error
    }
  }

  /**
   * Get posts from BGV database with filtering
   */
  async getPosts(filters?: PostFilters): Promise<BGVSocialPost[]> {
    try {
      // Start with basic query and add profile join if possible
      let query = supabase
        .from('social_posts')
        .select('*')

      // Try to add profile join - handle gracefully if schema doesn't match
      try {
        query = supabase
          .from('social_posts')
          .select(`
            *,
            social_profiles (
              name,
              platform,
              franchise_region
            )
          `)
      } catch (joinError) {
        console.warn('Profile join failed, using basic query:', joinError)
        query = supabase
          .from('social_posts')
          .select('*')
      }

      if (filters?.status) {
        query = query.in('status', filters.status)
      }

      if (filters?.profile_id) {
        query = query.eq('profile_id', filters.profile_id)
      }

      if (filters?.created_by) {
        // Convert string to UUID format if needed
        const createdByUuid = filters.created_by === 'admin-dashboard-test' 
          ? '00000000-0000-0000-0000-000000000001'
          : filters.created_by === 'admin-dashboard'
          ? '00000000-0000-0000-0000-000000000002'
          : filters.created_by
        query = query.eq('created_by', createdByUuid)
      }

      if (filters?.date_range) {
        query = query
          .gte('created_at', filters.date_range.start)
          .lte('created_at', filters.date_range.end)
      }

      const { data, error } = await query
        .order('scheduled_time', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })

      if (error) {
        // If join failed, try simple query
        if (error.code === '42703') {
          console.warn('Column not found, trying simple query')
          const { data: simpleData, error: simpleError } = await supabase
            .from('social_posts')
            .select('*')
            .order('scheduled_time', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false })
          
          if (simpleError) {
            throw simpleError
          }
          
          return simpleData || []
        }
        throw error
      }

      // Log to see the sort order
      console.log('Posts fetched, first 3:', data?.slice(0, 3).map(p => ({
        id: p.id,
        content: p.content?.substring(0, 30) + '...',
        scheduled_time: p.scheduled_time,
        created_at: p.created_at,
        status: p.status
      })))
      
      return data || []
    } catch (error) {
      console.error('BGV getPosts error:', error)
      throw error
    }
  }

  /**
   * Get social profiles from BGV database
   */
  async getProfiles(): Promise<BGVSocialProfile[]> {
    try {
      // First try with is_active column, fallback without it
      const query = supabase
        .from('social_profiles')
        .select('*')
        .order('name')

      // Check if is_active column exists by trying a query first
      try {
        const { data, error } = await query.eq('is_active', true)
        
        if (error && error.code === '42703') {
          // Column doesn't exist, query without it
          console.warn('is_active column not found, querying all profiles')
          const { data: allData, error: allError } = await supabase
            .from('social_profiles')
            .select('*')
            .order('name')
          
          if (allError) {
            throw allError
          }
          
          // Add default is_active value
          return (allData || []).map(profile => ({
            ...profile,
            is_active: true // Default to true
          }))
        }
        
        return data || []
      } catch (columnError) {
        // If column check fails, try without the filter
        console.warn('Fallback: querying all profiles without is_active filter')
        const { data, error } = await supabase
          .from('social_profiles')
          .select('*')
          .order('name')
        
        if (error) {
          throw error
        }
        
        // Add default is_active value
        return (data || []).map(profile => ({
          ...profile,
          is_active: true // Default to true
        }))
      }
    } catch (error) {
      console.error('BGV getProfiles error:', error)
      throw error
    }
  }

  /**
   * Get a specific post by ID
   */
  async getPost(postId: string): Promise<BGVSocialPost | null> {
    try {
      const { data, error } = await supabase
        .from('social_posts')
        .select(`
          *,
          social_profiles (
            name,
            platform,
            franchise_region
          )
        `)
        .eq('id', postId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // Post not found
        }
        throw error
      }

      return data
    } catch (error) {
      console.error('BGV getPost error:', error)
      throw error
    }
  }

  /**
   * Update post status
   */
  async updatePostStatus(postId: string, status: BGVSocialPost['status']): Promise<void> {
    try {
      const { error } = await supabase
        .from('social_posts')
        .update({
          status,
          last_status_change: new Date().toISOString()
        })
        .eq('id', postId)

      if (error) {
        throw error
      }
    } catch (error) {
      console.error('BGV updatePostStatus error:', error)
      throw error
    }
  }

  /**
   * Mark a post as test to prevent real publishing
   */
  private async markPostAsTest(postId: string): Promise<void> {
    try {
      const post = await this.getPost(postId)
      if (!post) {
        throw new Error('Post not found')
      }

      // Add TEST markers if not already present
      const content = post.content.startsWith('[TEST]') ? post.content : '[TEST] ' + post.content
      const caption = post.caption 
        ? (post.caption.startsWith('[TEST]') ? post.caption : '[TEST] ' + post.caption)
        : '[TEST POST - DO NOT PUBLISH]'

      const { error } = await supabase
        .from('social_posts')
        .update({
          content,
          caption,
          created_by: '00000000-0000-0000-0000-000000000001'
        })
        .eq('id', postId)

      if (error) {
        throw error
      }
    } catch (error) {
      console.error('BGV markPostAsTest error:', error)
      throw error
    }
  }

  /**
   * Subscribe to post updates in real-time
   */
  subscribeToPostUpdates(postId: string, callback: (post: BGVSocialPost) => void) {
    return supabase
      .channel(`post-${postId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'social_posts',
        filter: `id=eq.${postId}`
      }, (payload) => {
        callback(payload.new as BGVSocialPost)
      })
      .subscribe()
  }

  /**
   * Direct HTTP call to BGV edge functions (alternative method)
   */
  async callBGVFunction(functionName: string, payload?: any): Promise<any> {
    try {
      const session = await supabase.auth.getSession()
      const token = session.data.session?.access_token

      const response = await fetch(`${this.functionsUrl}/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || 'anonymous'}`,
        },
        body: JSON.stringify(payload || {})
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
      }

      return response.json()
    } catch (error) {
      console.error('BGV callBGVFunction error:', error)
      throw error
    }
  }

  /**
   * Get integration health status
   */
  async getHealthStatus(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy'
    database: boolean
    functions: boolean
    message: string
  }> {
    const { database, functions } = await this.testConnection()

    let status: 'healthy' | 'degraded' | 'unhealthy'
    let message: string

    if (database && functions) {
      status = 'healthy'
      message = 'BGV integration is fully operational'
    } else if (database || functions) {
      status = 'degraded'
      message = 'BGV integration has partial functionality'
    } else {
      status = 'unhealthy'
      message = 'BGV integration is not available'
    }

    return { status, database, functions, message }
  }
}

// Export singleton instance
export const bgvApi = new BGVApiService()

// Note: Using shared supabase client from @/lib/supabase - no separate export needed

// Utility functions
export async function quickHealthCheck(): Promise<boolean> {
  try {
    const { status } = await bgvApi.getHealthStatus()
    return status === 'healthy'
  } catch {
    return false
  }
}